import axios from "axios";
import {
    DarwinAvailabilityRequest,
    DarwinAvailabilityResponse,
    DarwinHeldTrip,
    DarwinTrip,
} from "../types/darwin";

// Movmo API configuration - proxy to Darwin to avoid CORS issues
const MOVMO_API_URL =
    process.env.REACT_APP_MOVMO_API_URL || "https://e2e.api.movmo.io";

// Search for flights via Movmo proxy to Darwin availability API
export const searchDarwinFlights = async (
    request: DarwinAvailabilityRequest,
): Promise<DarwinAvailabilityResponse> => {
    const response = await axios.post<DarwinAvailabilityResponse>(
        `${MOVMO_API_URL}/v1/darwin/perform-availability`,
        request,
        {
            headers: {
                "Content-Type": "application/json",
                "X-Api-Key": process.env.REACT_APP_MOVMO_API_KEY || "",
            },
        },
    );
    return response.data;
};

// Hold flight inventory — called on checkout page to reserve selected flights
export interface HoldFlightInventoryRequest {
    sessionId: string;
    flightsToHold: {
        marketingCarrierCode: string;
        origin: string;
        destination: string;
        flightNumber: string;
        departureDate: string;
        departureTime: string;
        arrivalTime: string;
        fareSummaryUid: string;
    }[];
    ptcHoldCounts: { ptc: string; holdCount: number }[];
    currencyCode: string;
}

export interface HoldFlightInventoryResponse {
    currencyCode: string;
    ptcHoldCounts: { ptc: string; holdCount: number }[];
    flightInventoryHeldDetails: {
        inventoryHoldUid: string;
        trip: DarwinHeldTrip;
        fareSummaryUid: string;
        holdExpiration: string;
    }[];
    sessionId: string;
}

export const holdFlightInventory = async (
    request: HoldFlightInventoryRequest,
): Promise<HoldFlightInventoryResponse> => {
    const response = await axios.post<HoldFlightInventoryResponse>(
        `${MOVMO_API_URL}/v1/darwin/hold-flight-inventory`,
        request,
        {
            headers: {
                "Content-Type": "application/json",
                "X-Api-Key": process.env.REACT_APP_MOVMO_API_KEY || "",
            },
        },
    );
    return response.data;
};

// Build a Darwin search request from form state
export const buildDarwinSearchRequest = (
    origin: string,
    destination: string,
    departureDateStart: string,
    departureDateEnd: string,
    seats: number,
): DarwinAvailabilityRequest => {
    return {
        seatsRequested: seats,
        showSoldoutFlights: false,
        currencyCode: "USD",
        availabilityReqDetails: [
            {
                origin,
                destination,
                departureDateStart,
                departureDateEnd,
            },
        ],
    };
};

// Find the minimum adult totalFare across all fareClasses/fares/ptcFares for a trip
// Filters to ADT (adult) fares only — infant/child fares have lower totals due to tax exemptions
export const getLowestFare = (trip: DarwinTrip): number => {
    let lowest = Infinity;
    for (const fareClass of trip.fareClasses) {
        for (const fare of fareClass.fares) {
            for (const ptcFare of fare.ptcFares) {
                if (ptcFare.ptcCode === "ADT" && ptcFare.totalFare < lowest) {
                    lowest = ptcFare.totalFare;
                }
            }
        }
    }
    return lowest === Infinity ? 0 : lowest;
};

// Sum all taxes on the ptcFare that has the lowest totalFare
export const getTotalTaxes = (trip: DarwinTrip): number => {
    let lowestFare = Infinity;
    let taxes = 0;
    for (const fareClass of trip.fareClasses) {
        for (const fare of fareClass.fares) {
            for (const ptcFare of fare.ptcFares) {
                if (ptcFare.totalFare < lowestFare) {
                    lowestFare = ptcFare.totalFare;
                    taxes = ptcFare.ptcTaxes.reduce(
                        (sum, tax) => sum + tax.amount,
                        0,
                    );
                }
            }
        }
    }
    return taxes;
};

// Format duration in minutes to "2H 20M"
export const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}M`;
    if (mins === 0) return `${hours}H`;
    return `${hours}H ${mins}M`;
};

// Format a time string "HH:MM" or "HH:MM:SS" to "1:00 PM"
export const formatTime = (timeStr: string): string => {
    const [hourStr, minuteStr] = timeStr.split(":");
    const hour = parseInt(hourStr, 10);
    const minute = minuteStr || "00";
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${displayHour}:${minute} ${period}`;
};

// Format a date string to "Fri, Mar 27"
// Handles "YYYY-MM-DD", "MM/DD/YYYY", "YYYYMMDD", or ISO strings
export const formatDate = (dateStr: string): string => {
    if (!dateStr) return "";
    let year: number, month: number, day: number;

    if (dateStr.includes("-") && dateStr.length >= 10) {
        // YYYY-MM-DD or ISO
        [year, month, day] = dateStr.substring(0, 10).split("-").map(Number);
    } else if (dateStr.includes("/")) {
        // MM/DD/YYYY
        const parts = dateStr.split("/").map(Number);
        [month, day, year] = parts;
    } else if (dateStr.length === 8) {
        // YYYYMMDD
        year = parseInt(dateStr.substring(0, 4), 10);
        month = parseInt(dateStr.substring(4, 6), 10);
        day = parseInt(dateStr.substring(6, 8), 10);
    } else {
        return dateStr; // fallback — return as-is
    }

    const date = new Date(year, month - 1, day);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
    });
};

// Hardcoded Aero airport data — terminal/FBO names match aero.com checkout display
export const AERO_AIRPORTS: Record<
    string,
    { code: string; city: string; name: string; terminal: string }
> = {
    MCO: {
        code: "MCO",
        city: "Orlando",
        name: "Orlando International Airport",
        terminal: "Aero Terminal",
    },
    JFK: {
        code: "JFK",
        city: "New York",
        name: "John F. Kennedy International Airport",
        terminal: "Terminal 4",
    },
    LAS: {
        code: "LAS",
        city: "Las Vegas",
        name: "Harry Reid International Airport",
        terminal: "Signature Aviation Las",
    },
    VNY: {
        code: "VNY",
        city: "Los Angeles",
        name: "Van Nuys Airport",
        terminal: "Aero Terminal",
    },
    SFO: {
        code: "SFO",
        city: "San Francisco",
        name: "San Francisco International Airport",
        terminal: "Signature Aviation SFO",
    },
    DFW: {
        code: "DFW",
        city: "Dallas",
        name: "Dallas Fort Worth International Airport",
        terminal: "Aero Terminal",
    },
    ASE: {
        code: "ASE",
        city: "Aspen",
        name: "Aspen/Pitkin County Airport",
        terminal: "Aero Terminal",
    },
    MIA: {
        code: "MIA",
        city: "Miami",
        name: "Miami International Airport",
        terminal: "Aero Terminal",
    },
    EGE: {
        code: "EGE",
        city: "Vail",
        name: "Eagle County Regional Airport",
        terminal: "Aero Terminal",
    },
    SUN: {
        code: "SUN",
        city: "Sun Valley",
        name: "Friedman Memorial Airport",
        terminal: "Aero Terminal",
    },
};
